import { useState, useCallback, useMemo } from 'react'
import { GridRowSelectionModel } from '@mui/x-data-grid'
import { SelectionData } from '../types'

export interface UseSelectionProps {
  onSelectionChange?: (
    selection: GridRowSelectionModel,
    selectionData?: SelectionData
  ) => void
  rows: any[]
}

export function useSelection({ onSelectionChange, rows }: UseSelectionProps) {
  const [selection, setSelection] = useState<GridRowSelectionModel>([])

  // 构建行ID到行数据的映射，用于快速查找
  const rowsMap = useMemo(() => {
    const map = new Map<string | number, any>()
    rows.forEach(row => {
      map.set(row.id, row)
    })
    return map
  }, [rows])

  // 获取选择数据
  const getSelectionData = useCallback(
    (selectedIds: GridRowSelectionModel): SelectionData | undefined => {
      if (!selectedIds || selectedIds.length === 0) {
        return {
          rows: [],
          count: 0,
          type: 'none'
        }
      }

      // 获取所有选中的行
      const selectedRows = selectedIds
        .map(id => rowsMap.get(id))
        .filter(row => !!row)

      // 检查选中行的类型
      const hasParent = selectedRows.some(row => row.isGroupRow)
      const hasChild = selectedRows.some(row => row.isChildRow)

      // 确定选择类型 - 简化为只考虑父行或子行，不考虑混合情况
      let type: 'parent' | 'child' | 'none' = 'none'
      if (hasParent) type = 'parent'
      else if (hasChild) type = 'child'

      return {
        rows: selectedRows,
        count: selectedRows.length,
        type
      }
    },
    [rowsMap]
  )

  const handleSelectionChange = useCallback(
    (newSelection: GridRowSelectionModel) => {
      setSelection(newSelection)

      if (onSelectionChange) {
        // 获取选中行的数据
        const selectionData = getSelectionData(newSelection)
        onSelectionChange(newSelection, selectionData)
      }
    },
    [onSelectionChange, getSelectionData]
  )

  return { selection, handleSelectionChange }
}
